<template>
  <div :class="{ 'ep-item--disabled': !!disabledReason }" class="ep-item">
    <span v-if="item.entity || item.icon" :class="avatarColor ? 'ep-avatar--colored' : `tone-${avatarTone}`" :style="avatarStyle"
          aria-hidden="true" class="ep-avatar">
      <v-icon v-if="avatarIcon" class="ep-avatar__icon" small>{{ avatarIcon }}</v-icon>
      <template v-else>{{ initials }}</template>
    </span>
    <div class="ep-body">
      <div class="ep-title-row">
        <span class="ep-title">{{ item.text }}</span>
        <span v-if="showCustomId" class="ep-id">{{ item.customId }}</span>
        <span v-for="(tag, i) in tags" :key="`tag-${i}`" :class="`tone-${tag.tone}`" :title="tag.title"
              class="tag tag--sm">
          <v-icon v-if="tag.icon" class="tag__icon" x-small>{{ tag.icon }}</v-icon>
          {{ tag.text }}
        </span>
      </div>
      <div v-if="subtitle" class="ep-sub">{{ subtitle }}</div>
      <div v-if="context" :class="item.contextTone ? `ep-context--${item.contextTone}` : ''" class="ep-context">
        {{ context }}
      </div>
      <div v-for="(line, i) in lines" :key="`line-${i}`" :class="line.tone ? `ep-context--${line.tone}` : ''"
           :title="line.title" class="ep-line">
        <v-icon v-if="line.icon" class="ep-line__icon" x-small>{{ line.icon }}</v-icon>
        <span class="ep-line__text">{{ line.text }}</span>
      </div>
      <div v-if="disabledReason" class="ep-disabled">
        <v-icon class="ep-line__icon" x-small>mdi-cancel</v-icon>
        <span>{{ disabledReason }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {PickerItem, PickerLine, PickerTag, PickerTone} from './entityPicker.types';
import {ENTITY_ICON, ENTITY_TONE, initialsOf} from './entityPicker.helper';
import {cssColor} from '@/components/common/autocomplete/autocomplete.helper';

/**
 * Shared list item of all entity pickers: avatar, title + reference number, status tags,
 * subtitle, a context line (e.g. "4,2 km · Di 14:00 kursfrei") and the reason why an item is disabled.
 * Works for plain {text, description, disabled} items as well.
 */
export default Vue.extend({
  props: {
    item: {type: Object as () => PickerItem, required: true},
  },
  computed: {
    initials(): string {
      return initialsOf(this.item.text);
    },
    avatarTone(): PickerTone {
      return this.item.entity ? ENTITY_TONE[this.item.entity] : 'muted';
    },
    avatarIcon(): string | undefined {
      return this.item.icon || (this.item.entity ? ENTITY_ICON[this.item.entity] : undefined);
    },
    avatarColor(): string {
      return cssColor(this.item.avatarColor);
    },
    avatarStyle(): Record<string, string> | undefined {
      return this.avatarColor ? {backgroundColor: this.avatarColor} : undefined;
    },
    showCustomId(): boolean {
      return !!this.item.customId && !this.item.text.includes(this.item.customId);
    },
    tags(): PickerTag[] {
      return this.item.tags || [];
    },
    lines(): PickerLine[] {
      return this.item.lines || [];
    },
    subtitle(): string {
      if (typeof this.item.subtitle === 'string') {
        return this.item.subtitle;
      }
      const description = this.item.description || '';
      // The reference number is already shown next to the title
      if (description && description === this.item.customId) {
        return '';
      }
      // Former behaviour: a string reason replaced the description; it is now shown in its own line
      if (description && description === this.disabledReason) {
        return '';
      }
      return description;
    },
    context(): string {
      return (this.item.context || []).filter(Boolean).join(' · ');
    },
    disabledReason(): string {
      if (typeof this.item.disabled === 'string' && this.item.disabled) {
        return this.item.disabled;
      }
      if (this.item.disabled === true && this.item.disabledReason) {
        return this.item.disabledReason;
      }
      return '';
    },
  },
});
</script>

<style scoped>
.ep-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  width: 100%;
  padding: 2px 0;
}

.ep-avatar {
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 30px;
  height: 30px;
  margin-top: 1px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .02em;
}

.ep-avatar__icon {
  color: inherit !important;
}

.ep-avatar--colored {
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, .08);
}

.ep-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
}

.ep-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  min-width: 0;
}

.ep-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ep-id {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  opacity: .6;
}

.ep-sub, .ep-context, .ep-line, .ep-disabled {
  font-size: 12px;
  line-height: 1.35;
  opacity: .72;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ep-context {
  opacity: .85;
}

.ep-line {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  white-space: normal;
}

.ep-line__icon {
  color: inherit !important;
  margin-top: 1px;
}

.ep-line__text {
  min-width: 0;
}

.ep-context--good {
  color: #1B5E20;
  opacity: 1;
}

.ep-context--warn {
  color: #7A4F00;
  opacity: 1;
}

.ep-context--info {
  color: #0D47A1;
  opacity: 1;
}

.ep-context--bad {
  color: #B71C1C;
  opacity: 1;
}

.ep-disabled {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #B71C1C;
  opacity: 1;
  font-weight: 500;
}

.theme--dark .ep-context--good {
  color: #A5D6A7;
}

.theme--dark .ep-context--warn {
  color: #FFD180;
}

.theme--dark .ep-context--info {
  color: #90CAF9;
}

.theme--dark .ep-context--bad, .theme--dark .ep-disabled {
  color: #EF9A9A;
}
</style>
<style src="@/components/common/modern/modern.css"></style>
